import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { TopoLines } from './TopoLines';
import { PAGE_IMAGES } from '../data/assets';
import { CardPreview, FlowchartPreview, CodeColumnsPreview, WorksheetPreview, GuessProvePreview, FieldCardPreview } from './PagePreviews';
type Page = {
  key: string;
  src: string;
  preview: React.ReactNode;
  alt: string;
  caption: string;
};

/**
 * "A look inside" — tipos de página del pack (Índices DPS65). Si hay captura
 * real en data/assets.ts se muestra la imagen; si no, la vista previa en código.
 */
const pages: Page[] = [{
  key: 'card',
  src: PAGE_IMAGES.card,
  preview: <CardPreview data-id="element-142" data-mp="1mxcxlo36lj8b" />,
  alt: 'A Split-and-Prove Card for a ground fault alarm with the five checkpoints',
  caption: 'Split-and-Prove Card: one fault, five checkpoints'
}, {
  key: 'flowchart',
  src: PAGE_IMAGES.flowchart,
  preview: <FlowchartPreview data-id="element-143" data-mp="1dwn26lhu1uv4" />,
  alt: 'A low-production flowchart from portal alert to the faulty string',
  caption: 'Symptom flowcharts: from portal alert to the faulty string'
}, {
  key: 'codeColumns',
  src: PAGE_IMAGES.codeColumns,
  preview: <CodeColumnsPreview data-id="element-144" data-mp="k13x4y2lrish" />,
  alt: 'A code cross-reference table with US, Canada, UK and Australia columns',
  caption: 'Code Column Method: one check, four countries'
}, {
  key: 'worksheet',
  src: PAGE_IMAGES.worksheet,
  preview: <WorksheetPreview data-id="element-145" data-mp="3nhu69lthebye" />,
  alt: 'The WS-04 String Measurement Log worksheet',
  caption: 'Worksheets WS-01 to WS-40, printable or fillable'
}, {
  key: 'guessProve',
  src: PAGE_IMAGES.guessProve,
  preview: <GuessProvePreview data-id="element-146" data-mp="13t0ll8cv3gq" />,
  alt: 'A comparison of the same service call done by guessing and by proving',
  caption: 'Guess vs prove: the same call done two ways'
}, {
  key: 'fieldCard',
  src: PAGE_IMAGES.fieldCard,
  preview: <FieldCardPreview data-id="element-147" data-mp="2zsrqykmbkv72" />,
  alt: 'The Solar Service Field Card with shutdown order and stop rules',
  caption: 'Field cards: shutdown order and stop rules'
}];
export function LookInside() {
  const trackRef = useRef<HTMLUListElement>(null);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(true);
  const updateArrows = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    setCanPrev(el.scrollLeft > 4);
    setCanNext(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);
  useEffect(() => {
    updateArrows();
    window.addEventListener('resize', updateArrows);
    return () => window.removeEventListener('resize', updateArrows);
  }, [updateArrows]);
  const scrollByCard = (direction: 1 | -1) => {
    const el = trackRef.current;
    if (!el) return;
    const card = el.querySelector('li');
    const step = card ? card.getBoundingClientRect().width + 16 : el.clientWidth * 0.8;
    el.scrollBy({
      left: step * direction,
      behavior: 'smooth'
    });
  };
  const arrowClass = 'absolute top-[42%] z-10 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-paper-dark bg-white text-pine shadow-card transition-[opacity,background-color,transform] duration-150 ease-out hover:bg-paper active:scale-95 focus:outline-none focus-visible:ring-4 focus-visible:ring-ember/50 disabled:pointer-events-none disabled:opacity-0';
  return <section aria-labelledby="peek-heading" className="topo-light w-full bg-paper" data-id="element-148" data-mp="26h7g3r4r1o6t">
      <div className="mx-auto max-w-6xl px-5 pt-14 sm:pt-20" data-id="element-149" data-mp="21jxt0i4u1om5">
        <div className="text-center" data-id="element-150" data-mp="1wiqjadj4qfcu">
          <p className="eyebrow text-ember-deep" data-id="element-151" data-mp="1whoy3g8egche">A look inside</p>
          <h2 id="peek-heading" className="trail-underline mt-3 font-display text-[34px] leading-tight text-pine sm:text-6xl" data-id="element-152" data-mp="1rjf0z03471t5">
            Pages built for the roof, not the shelf
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-base text-pine-soft" data-id="element-153" data-mp="3lxobj9j1wo2u">
            Not a list of codes. Horizontal pages, one fault per page, readable
            on your phone in full sun — here&apos;s how they&apos;re laid out.
          </p>
        </div>

        <div className="relative mt-8" data-id="element-154" data-mp="3tzfg4e69zet4">
          <TopoLines className="pointer-events-none absolute -left-10 -top-6 hidden h-40 w-40 text-bark/10 lg:block" data-id="element-155" data-mp="3tydvpj3ddfyq" />
          <button type="button" onClick={() => scrollByCard(-1)} disabled={!canPrev} aria-label="Previous page" className={`${arrowClass} left-0 sm:-left-2`} data-id="element-156" data-mp="3g17dbpmk5ja1">
            <ChevronLeftIcon className="h-6 w-6" aria-hidden="true" data-id="element-157" data-mp="3azrve6fofsvv" />
          </button>
          <button type="button" onClick={() => scrollByCard(1)} disabled={!canNext} aria-label="Next page" className={`${arrowClass} right-0 sm:-right-2`} data-id="element-158" data-mp="2wb5azqb70axs">
            <ChevronRightIcon className="h-6 w-6" aria-hidden="true" data-id="element-159" data-mp="2na785bchq9gs" />
          </button>
          <ul ref={trackRef} onScroll={updateArrows} className="-mx-5 flex snap-x snap-mandatory gap-4 overflow-x-auto px-5 pb-5 [scrollbar-width:thin]" data-id="element-160" data-mp="2sa1xhagmaqzq">
            {pages.map(page => <li key={page.key} className="shrink-0 basis-[84%] snap-start sm:basis-[360px]" data-id="element-161" data-mp="23q8ddd864qen">
                <figure className="overflow-hidden rounded-2xl border border-paper-dark bg-white shadow-card" data-id="element-162" data-mp="2dn395od24cp8">
                  <div className="relative aspect-[16/10] w-full overflow-hidden bg-paper-dark" data-id="element-163" data-mp="2rm2wmsou2ljt">
                    {page.src ? <img src={page.src} alt={page.alt} loading="lazy" className="absolute inset-0 h-full w-full object-cover object-top" data-id="element-164" data-mp="2mmq905q51ag6" /> : <div role="img" aria-label={page.alt} className="absolute inset-0" data-id="element-165" data-mp="3ae5h6f4lilzc">
                        {page.preview}
                      </div>}
                  </div>
                  <figcaption className="border-t border-paper-dark px-3.5 py-3 text-[13px] font-semibold leading-snug text-pine" data-id="element-166" data-mp="231nzi7go09ex">
                    {page.caption}
                  </figcaption>
                </figure>
              </li>)}
          </ul>
          <p className="field-note mt-1 text-center text-xl text-pine-soft sm:text-2xl" data-id="element-167" data-mp="2grrhuncfbiz3">
            159 cards · 40 worksheets · 14 named frameworks across six volumes
          </p>
        </div>
      </div>
    </section>;
}